import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import { ICountry } from '@/interfaces/general/ICountry.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import {
    findCountryByCountryId,
    getCountrySelectionValue,
    getPlantCountries,
    getPlantCountryIds,
} from '@/helpers/powerMarkets/countrySelectorOptions.ts';

const COUNTRY_FALLBACK_ORDER = ['Gb', 'France'];

export interface InitialCountrySelectionOptions {
    availableCountryIds: Array<string>;
    explicitCountryId?: string | null;
    queryCountryId?: string | null;
    siteDefaultCountryId?: string | null;
}

export interface CountrySelectorSelectionOptions {
    countries?: Array<ICountry>;
    plantsOnly?: boolean;
    specificCountryAccess?: boolean;
    europeAccess?: boolean;
    explicitCountryId?: string | null;
    queryCountryId?: string | null;
    siteDefaultCountryId?: string | null;
}

export function resolveAvailableCountryId (
    countryId: string | null | undefined,
    availableCountryIds: Array<string>,
): string | undefined {
    if (typeof countryId !== 'string' || countryId.length === 0) return undefined;

    return availableCountryIds.find(availableCountryId => availableCountryId.toLowerCase() === countryId.toLowerCase());
}

function resolveFallbackCountryId (
    countryIds: Array<string>,
    availableCountryIds: Array<string>,
): string | undefined {
    return countryIds
        .map(countryId => resolveAvailableCountryId(countryId, availableCountryIds))
        .find(availableCountryId => availableCountryId !== undefined);
}

export function resolveInitialCountryId ({
    availableCountryIds,
    explicitCountryId,
    siteDefaultCountryId,
}: InitialCountrySelectionOptions): string | undefined {
    return resolveAvailableCountryId(explicitCountryId, availableCountryIds)
        ?? resolveAvailableCountryId(siteDefaultCountryId, availableCountryIds)
        ?? resolveFallbackCountryId(COUNTRY_FALLBACK_ORDER, availableCountryIds)
        ?? availableCountryIds[0];
}

export function resolveInitialCountryIdWithQuery ({
    availableCountryIds,
    explicitCountryId,
    queryCountryId,
    siteDefaultCountryId,
}: InitialCountrySelectionOptions): string | undefined {
    return resolveAvailableCountryId(explicitCountryId, availableCountryIds)
        ?? resolveAvailableCountryId(queryCountryId, availableCountryIds)
        ?? resolveAvailableCountryId(siteDefaultCountryId, availableCountryIds)
        ?? resolveFallbackCountryId(COUNTRY_FALLBACK_ORDER, availableCountryIds)
        ?? availableCountryIds[0];
}

export function getAvailableCountriesForSelection ({
    countries = [],
    plantsOnly = false,
    specificCountryAccess = false,
    europeAccess = false,
}: CountrySelectorSelectionOptions): Array<ICountry> {
    let availableCountries = plantsOnly ? getPlantCountries(countries) : countries;

    if (specificCountryAccess) availableCountries = availableCountries.filter(country => country.hasSpecificAccess);
    if (europeAccess) availableCountries = availableCountries.filter(country => country.hasEuropeAccess);

    return availableCountries;
}

export function getAvailableCountryIdsForSelection (options: CountrySelectorSelectionOptions): Array<string> {
    const availableCountries = getAvailableCountriesForSelection(options);

    if (options.plantsOnly) return getPlantCountryIds(availableCountries);

    return availableCountries.flatMap(country => (country.mainSubCountryId ? [country.id, country.mainSubCountryId] : [country.id]));
}

function resolveInitialCountrySelectionValue (
    options: CountrySelectorSelectionOptions,
    resolveCountryId: (options: InitialCountrySelectionOptions) => string | undefined,
): IIdName | undefined {
    const availableCountries = getAvailableCountriesForSelection(options);
    const initialCountryId = resolveCountryId({
        ...options,
        availableCountryIds: getAvailableCountryIdsForSelection(options),
    });
    const initialCountry = findCountryByCountryId(availableCountries, initialCountryId);

    return initialCountry ? getCountrySelectionValue(initialCountry) : undefined;
}

export function resolveInitialCountrySelection (options: CountrySelectorSelectionOptions): IIdName | undefined {
    return resolveInitialCountrySelectionValue(options, resolveInitialCountryId);
}

export function useInitialCountrySelection (): {
    resolveInitialCountryId: (options: InitialCountrySelectionOptions) => string | undefined;
    resolveInitialCountryIdWithQuery: (options: InitialCountrySelectionOptions) => string | undefined;
    getAvailableCountryIds: (options?: CountrySelectorSelectionOptions) => Array<string>;
    resolveInitialCountrySelection: (options?: CountrySelectorSelectionOptions) => IIdName | undefined;
} {
    const userSettingsStore = useUserSettingsStore();
    const dataStore = useDataStore();

    function getSelectionOptionsWithDefaults (options: CountrySelectorSelectionOptions = {}): CountrySelectorSelectionOptions {
        return {
            ...options,
            countries: options.countries ?? dataStore.staticStore.countries,
            siteDefaultCountryId: options.siteDefaultCountryId ?? userSettingsStore.userCountry,
        };
    }

    function getAvailabilityOptions (options: CountrySelectorSelectionOptions = {}): CountrySelectorSelectionOptions {
        return {
            countries: options.countries ?? dataStore.staticStore.countries,
            plantsOnly: options.plantsOnly,
            specificCountryAccess: options.specificCountryAccess,
            europeAccess: options.europeAccess,
        };
    }

    function getInitialCountrySelectionOptions (options: InitialCountrySelectionOptions): InitialCountrySelectionOptions {
        return {
            ...options,
            siteDefaultCountryId: options.siteDefaultCountryId ?? userSettingsStore.userCountry,
        };
    }

    return {
        resolveInitialCountryId: (options: InitialCountrySelectionOptions) => resolveInitialCountryId(getInitialCountrySelectionOptions(options)),
        resolveInitialCountryIdWithQuery: (options: InitialCountrySelectionOptions) => resolveInitialCountryIdWithQuery(getInitialCountrySelectionOptions(options)),
        getAvailableCountryIds: (options: CountrySelectorSelectionOptions = {}) => getAvailableCountryIdsForSelection(getAvailabilityOptions(options)),
        resolveInitialCountrySelection: (options: CountrySelectorSelectionOptions = {}) => resolveInitialCountrySelection(getSelectionOptionsWithDefaults(options)),
    };
}
